// TODO: i18n remove MAP and translate the file
import {
  APPRENTISSAGE,
  CDD,
  CONTRACT_TYPE,
  ContractTypeArray,
  EXTRA,
  INTERIM,
  INTERN,
  SEASONAL,
  type WorkPermitType
} from '@libs/data-access/entities'
import { TimeoffComputedStatus, TimeoffStatus } from '@libs/data-access/queries'
import { useCommonAppTranslation } from '@libs/utils/translations'
import { useAuthorizedWorkPermitTypes } from 'snap-redux/modules/countryConfigurations/selectors'

export const LANGUAGE_LOCALES = [
  {
    value: 'FR',
    label: 'Français'
  },
  {
    value: 'ES',
    label: 'Español'
  },
  {
    value: 'EN',
    label: 'English'
  },
  {
    value: 'IT',
    label: 'Italiano'
  }
]

type Props = {
  authorizedContractTypes: string[]
}

type ContractTypes = {
  value: string
  label: string
}

export const useContractTypes = ({
  authorizedContractTypes
}: Props): ContractTypes[] => {
  const { t } = useCommonAppTranslation()

  return ContractTypeArray.filter(type =>
    authorizedContractTypes.includes(type)
  ).map(type => ({
    value: type,
    label: t(CONTRACT_TYPE[type])
  }))
}

export const WORK_PERMIT_TYPES: { [key in WorkPermitType]: string } = {
  residence_permit: 'contract.info.form.workPermit.type.residencePermit',
  resident_card: 'contract.info.form.workPermit.type.residentCard',
  temporary_residence_permit:
    'contract.info.form.workPermit.type.temporaryResidencePermit',
  long_residence_permit:
    'contract.info.form.workPermit.type.longResidencePermit',
  pluriannual_residence_permit:
    'contract.info.form.workPermit.type.pluriannualResidencePermit',
  provisional_residence_authorization:
    'contract.info.form.workPermit.type.provisionalResidenceAuthorization',
  residence_permit_application_receipt:
    'contract.info.form.workPermit.type.residencePermitApplicationReceipt',
  protection_recognition_receipt:
    'contract.info.form.workPermit.type.protectionRecognitionReceipt',
  nie: 'contract.info.form.workPermit.type.nie',
  passport: 'contract.info.form.workPermit.type.passport',
  work_permit: 'contract.info.form.workPermit.type.work_permit',
  young_graduate_temporary_permit:
    'contract.info.form.workPermit.type.young_graduate_temporary_permit',
  short_shengen_visa: 'contract.info.form.workPermit.type.short_shengen_visa',
  posted_worker_for_european_service_provider_permit:
    'contract.info.form.workPermit.type.posted_worker_for_european_service_provider_permit',
  vacation_work_visa: 'contract.info.form.workPermit.type.vacation_work_visa',
  work_authorization_receipt:
    'contract.info.form.workPermit.type.work_authorization_receipt',
  job_search_or_entrepreneurship_permit:
    'contract.info.form.workPermit.type.job_search_or_entrepreneurship_permit',
  algerian_student_permit:
    'contract.info.form.workPermit.type.algerian_student_permit',
  student_permit: 'contract.info.form.workPermit.type.student_permit',
  posted_worker_permit:
    'contract.info.form.workPermit.type.posted_worker_permit',
  seasonal_worker_permit:
    'contract.info.form.workPermit.type.seasonal_worker_permit',
  temporary_worker_permit:
    'contract.info.form.workPermit.type.temporary_worker_permit',
  employee_permit: 'contract.info.form.workPermit.type.employee_permit'
}

const DISABLED_WORK_PERMIT_TYPES: { [key in WorkPermitType]: boolean } = {
  residence_permit: false,
  resident_card: false,
  temporary_residence_permit: false,
  long_residence_permit: true,
  pluriannual_residence_permit: true,
  provisional_residence_authorization: false,
  residence_permit_application_receipt: true,
  protection_recognition_receipt: true,
  nie: false,
  passport: false,
  work_permit: false,
  young_graduate_temporary_permit: false,
  short_shengen_visa: false,
  posted_worker_for_european_service_provider_permit: false,
  vacation_work_visa: false,
  work_authorization_receipt: false,
  job_search_or_entrepreneurship_permit: false,
  algerian_student_permit: false,
  student_permit: false,
  posted_worker_permit: false,
  seasonal_worker_permit: false,
  temporary_worker_permit: false,
  employee_permit: false
}

export const useWorkPermitTypes = () => {
  const authorizedWorkPermitTypes = useAuthorizedWorkPermitTypes()

  return Object.keys(WORK_PERMIT_TYPES)
    .filter(key => authorizedWorkPermitTypes.includes(key))
    .map(key => ({
      value: key,
      label: WORK_PERMIT_TYPES[key],
      disabled: DISABLED_WORK_PERMIT_TYPES[key]
    }))
}

export const PermitWithAuthorization: WorkPermitType[] = [
  'short_shengen_visa',
  'algerian_student_permit',
  'student_permit',
  'employee_permit',
  'temporary_worker_permit',
  'seasonal_worker_permit'
]

export const PermitOtherJob: WorkPermitType[] = [
  'employee_permit',
  'temporary_worker_permit',
  'seasonal_worker_permit'
]

export const SHOW_END_DATE_CONTRACT_TYPE = [
  CDD,
  EXTRA,
  INTERN,
  INTERIM,
  SEASONAL,
  APPRENTISSAGE
]

export const MANDATORY_END_DATE_CONTRACT_TYPE = [CDD, INTERIM, INTERN, SEASONAL]

export const timeoffStatusMapping = {
  [TimeoffStatus.approved]: {
    title: 'timeoff.status.accepted',
    badgeType: 'semantic.green'
  },
  [TimeoffStatus.declined]: {
    title: 'timeoff.status.declined',
    badgeType: 'semantic.red'
  },
  [TimeoffStatus.pending]: {
    title: 'timeoff.status.pending',
    badgeType: 'semantic.orange'
  },
  [TimeoffComputedStatus.expired]: {
    title: 'timeoff.status.expired',
    badgeType: 'semantic.red'
  },
  [TimeoffComputedStatus.deleted]: {
    title: 'timeoff.status.deleted',
    badgeType: 'semantic.red'
  }
}
